import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useV2UpdatePromptOverrideMutation } from "@generated";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { AdminFormPage } from "@components/CatalogIndex";
import PromptOverrideForm from "../common/Form/Form";
import { INITIAL_FORM_STATE, PromptOverrideFormState } from "../common/types";
import { formatPromptOverrideData } from "../common/helpers";
import { useGetPromptOverride } from "../common/api";
import "../style.css";

const FORM_ID = "prompt-override-form";

const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const overrideId = id || "";
  const [updatePromptOverride] = useV2UpdatePromptOverrideMutation();

  const {
    promptOverride,
    isLoading: isFetching,
    refetch,
  } = useGetPromptOverride(overrideId);

  const [formState, setFormState] =
    useState<PromptOverrideFormState>(INITIAL_FORM_STATE);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showFailAlert, setShowFailAlert] = useState(false);

  useEffect(() => {
    if ((location.state as any)?.refetch) {
      refetch();
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  useEffect(() => {
    if (promptOverride && overrideId) {
      setFormState((prev) => {
        if (prev.id) return prev;
        return { ...promptOverride };
      });
    }
  }, [promptOverride, overrideId]);

  const handleSave = () => {
    if (!formState.promptType || !formState.content) return;

    setLoading(true);
    const data = formatPromptOverrideData(formState);

    updatePromptOverride({ id: overrideId, body: { data } })
      .unwrap()
      .then(() => {
        setSuccessMessage("Prompt override updated successfully!");
        setShowSuccessAlert(true);
        refetch();
      })
      .catch((error) => {
        console.error("API Error:", error);
        setErrorMessage(
          error?.data?.errors?.[0]?.detail || "An error occurred."
        );
        setShowFailAlert(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (isFetching) {
    return <SmallSpinner />;
  }

  return (
    <>
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      <AdminFormPage
        title="Edit Prompt Override"
        formId={FORM_ID}
        onCancel={() =>
          navigate("/admin/prompt_overrides", { state: { refetch: true } })
        }
        cancelLabel="Back"
        isSubmitting={loading}
      >
        <PromptOverrideForm
          formId={FORM_ID}
          formState={formState}
          setFormState={setFormState}
          editing={true}
          onSubmit={handleSave}
        />
      </AdminFormPage>
    </>
  );
};

export default Edit;
